import * as queryString from 'query-string';
import React from 'react';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import _ from 'underscore';

import { BrowserTitleService } from '~commonInfra/services/BrowserTitleService';
import { MapDispatchToProps } from '~commonInfra/utils/ConnectUtils';
import { Location } from '~commonInfra/utils/Location';
import { contentCountsUpToDate } from '~src/spacesPlatform/actions/ContentCountsActions';
import { enterSpace } from '~src/spacesPlatform/actions/EnterSpaceAction';
import { setContentFilterNoRequestContent } from '~src/spacesPlatform/actions/filters/FilterActions';
import { requestSpaceContents } from '~src/spacesPlatform/actions/RequestSpaceContentsActions';
import { updateBreadcrumbHref } from '~src/spacesPlatform/actions/UpdateBreadcrumbHref';
import { populateContentBreadcrumbs } from '~src/spacesPlatform/components/breadcrumbs/BreadcrumbHelpers';
import { getContentTypesFromQueryParam } from '~src/spacesPlatform/components/Explorer/ExplorerUrlBindings';
import { ConnectedSpaceInfoProvider } from '~src/spacesPlatform/components/Explorer/helpers/SpaceInfoProvider';
import { SpacesSiteUrls } from '~src/spacesPlatform/components/spaceComponents/SpacesSiteUrls';
import { updateOrderQueryParamsIfMismatched } from '~src/spacesPlatform/sagas/content/SortContentSagas';
import { SpaceInfo, SpaceItemForNonEntitySpace } from '~src/spacesPlatform/state/SpaceInfo';

import { ConnectedQueryParamUpdateProvider } from '../../../bootstrap/routerHelpers/QueryParamUpdateProvider';
import { ContentFilterKeyName, ContentFiltersKeys, ContentTypeFilterKey } from '../../../spacesPlatform/models/ContentFilters';
import { SiteSpaceContentType } from '../../../spacesPlatform/state/SiteSpaceContentTypeState';
import { SiteSpace, SiteSpaceProps } from './SiteSpace';

export interface SiteSpaceProviderComponentOwnProps {
  readonly siteName: string;
  readonly siteSpaceContentType: SiteSpaceContentType;
  readonly urls: SpacesSiteUrls;
  readonly routeLocationSearch: string;
  readonly renderSpace: (props: SiteSpaceProps) => JSX.Element;
}

export interface SiteSpaceProviderDispatchProps {
  readonly contentCountsUpToDate: () => void;
  readonly enterSpace: (spaceInfo: SpaceInfo) => void;
  readonly requestSpaceContents: () => void;
  readonly setContentFilterNoRequestContent: (filterType: ContentFilterKeyName, value: string) => void;
  readonly updateBreadcrumbHref: (href: string) => void;
}

export type SiteSpaceProviderComponentProps = SiteSpaceProviderComponentOwnProps & SiteSpaceProviderDispatchProps;

export class SiteSpaceProviderComponent extends React.PureComponent<SiteSpaceProviderComponentProps> {

  public componentDidMount() {
    populateContentBreadcrumbs([], this.props.urls);
    this.props.requestSpaceContents();
    BrowserTitleService.setTitleToServerName();

    // populateContentBreadcrumbs sets the 1st breadcrumb to /explore, in case you navigate directly to a page
    // without going through the site space (e.g., navigate directly to a workbook space). We want to override
    // that breadcrumb with the current url if you do visit the Site Space so that we store the correct breadcrumb.
    this.props.updateBreadcrumbHref(Location.instance.href());
  }

  public componentDidUpdate(prevProps: SiteSpaceProviderComponentProps) {
    if ((!_.isUndefined(this.props.siteName) && this.props.siteName !== prevProps.siteName) ||
        this.props.siteSpaceContentType !== prevProps.siteSpaceContentType) {

      const currentQueryValues = queryString.parse(this.props.routeLocationSearch);
      const previousQueryValues = queryString.parse(prevProps.routeLocationSearch);

      // make sure the filters get updated from the url. TFSID 809212
      for (const filterType of _.keys(ContentFiltersKeys)) {
        const value = currentQueryValues[filterType];
        this.props.setContentFilterNoRequestContent(filterType as ContentFilterKeyName, value? value + '' : undefined);
      }

      const spaceInfo: SpaceInfo = {
        item: SpaceItemForNonEntitySpace,
        spaceType: 'site',
        subspaceName: this.props.siteSpaceContentType
      };
      this.props.enterSpace(spaceInfo);

      updateOrderQueryParamsIfMismatched([], spaceInfo);

      // If we just switched tabs and search filters are the same, we know content counts are up to date.
      // They will get set to false by the above EnterSpaceAction.
      if(previousQueryValues[ContentFiltersKeys.search] === currentQueryValues[ContentFiltersKeys.search]) {
        this.props.contentCountsUpToDate();
      }

      this.props.updateBreadcrumbHref(Location.instance.href());

      this.props.requestSpaceContents();
    }
  }

  public render() {
    return this.props.renderSpace({
      urls: this.props.urls,
      siteSpaceContentType: this.props.siteSpaceContentType,
    });
  }
}

const mapDispatchToProps: MapDispatchToProps<SiteSpaceProviderDispatchProps> = (dispatch) => bindActionCreators({
  contentCountsUpToDate,
  requestSpaceContents,
  enterSpace,
  setContentFilterNoRequestContent,
  updateBreadcrumbHref
}, dispatch);

export const ConnectedSiteSpaceProvider: React.ComponentClass<SiteSpaceProviderComponentOwnProps>
  = connect(undefined, mapDispatchToProps)(SiteSpaceProviderComponent);

const renderSpace = (props: SiteSpaceProps): JSX.Element => <SiteSpace {...props} />;

export interface SiteSpaceProviderProps {
  readonly siteName: string;
  readonly routeLocationPath: string;
  readonly routeLocationSearch: string;
  readonly siteSpaceContentType: SiteSpaceContentType;
  readonly urls: SpacesSiteUrls;
  // Optional override for the space rendered inside the provider. Defaults to the standard SiteSpace;
  // Content Relay passes a dedicated tabbed space here. The provider still owns SpaceInfo + content fetch.
  readonly renderSpace?: (props: SiteSpaceProps) => JSX.Element;
}

export const SiteSpaceProvider: React.FunctionComponent<SiteSpaceProviderProps> = React.memo((props) => {

  let subspaceName = props.siteSpaceContentType;
  if (subspaceName === 'datasource') {
    const currentQueryValues = queryString.parse(props.routeLocationSearch);
    const contentTypesParam = currentQueryValues[ContentTypeFilterKey];
    const contentTypes = getContentTypesFromQueryParam(contentTypesParam, []);
    if (contentTypes.length === 1 && contentTypes[0] === 'dataconnection') {
      subspaceName = 'dataconnection';
    }
  }

  const SiteSpaceInfo: SpaceInfo = {
    item: SpaceItemForNonEntitySpace,
    spaceType: 'site',
    subspaceName
  };
  const siteSpaceProps = {
    siteName: props.siteName,
    urls: props.urls,
    siteSpaceContentType: subspaceName,
    routeLocationSearch: props.routeLocationSearch,
    renderSpace: props.renderSpace || renderSpace
  };

  return (
    <ConnectedSpaceInfoProvider spaceInfo={SiteSpaceInfo}>
      <ConnectedQueryParamUpdateProvider
        routeLocationPath={props.routeLocationPath}
        routeLocationSearch={props.routeLocationSearch}
        shouldUpdateBreadcrumbs={true}
        shouldUpdateContentTypes={true}
        shouldUpdateFilters={true}
        shouldUpdateSort={true}
      >
        <ConnectedSiteSpaceProvider {...siteSpaceProps} />
      </ConnectedQueryParamUpdateProvider>
    </ConnectedSpaceInfoProvider>
  );
});
